import { toNDigits } from '@mail/cross-sizes-utils';
import { logger } from 'lib/logger';
import { generateImageApiCall } from 'reactApp/api/aiGeneration/GenerateImageApiCall';
import { aiImageGenerationFreeLimit, aiImageGenerationPaidLimit } from 'reactApp/appHelpers/featuresHelpers/features/aiImageGeneration';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import {
    addGenerationRequest,
    cancelImageGeneration,
    generateImageEnd,
    generateImageStart,
    incrementImageGenerationPercentage,
    resetImageGeneration,
    saveGeneratedImage,
} from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.actions';
import {
    aiPhotoGenerationFolderId,
    aiPhotoGenerationFolderName,
    aiPhotoGenerationFolderPathFromRoot,
} from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.consts';
import {
    addAiGenerationRequest,
    aiPhotoGenerationHelper,
    checkAiPhotoGenerationExists,
    getAiPhotoGenerationImageFileName,
} from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.helpers';
import { getHomeFoldersTree } from 'reactApp/modules/home/home.selectors';
import { createFolderRequest } from 'reactApp/modules/modifying/sagas/createFolder.saga';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { saveDocument } from 'reactApp/modules/uploading/uploading.module';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { closeFullScreenAiPhotoGeneration } from 'reactApp/ui/AiPhotoGeneration/Screen/FullScreenAiPhotoGeneration';
import { base64ToByteArray } from 'reactApp/utils/base64';
import { sendXray } from 'reactApp/utils/ga';
import { opener } from 'reactApp/utils/opener';
import { type Channel, channel } from 'redux-saga';
import { takeLatest } from 'redux-saga/effects';
import { call, cancel, delay, fork, put, select, take } from 'typed-redux-saga';

import { EStorageType } from '../storage/storage.types';

function* handleGenerateImageStart({ payload }: ReturnType<typeof generateImageStart>) {
    const { prompt, style } = payload;
    const abortController = new AbortController();

    const intervalTask = yield* fork(function* () {
        while (true) {
            yield* delay(500);
            yield* put(incrementImageGenerationPercentage());
        }
    });

    const cancelWatcher = yield* fork(function* () {
        yield* take(cancelImageGeneration.toString());
        abortController.abort();
    });

    try {
        const startTime = performance.now();
        const resp = yield* call(generateImageApiCall, { prompt, style, signal: abortController.signal });
        if (resp.status === 200) {
            const endTime = performance.now() - startTime;
            const seconds = Math.round(endTime / 1000);

            const byteArray = base64ToByteArray(resp.data.image);
            aiPhotoGenerationHelper.saveImageData(byteArray);

            const size = toNDigits({ bytes: byteArray.byteLength, maxDigitsCount: 3 });

            const blob = new Blob([byteArray], { type: 'image/jpg' });
            const image = URL.createObjectURL(blob);

            yield* put(addGenerationRequest());
            yield* put(
                generateImageEnd({
                    image,
                    size,
                })
            );

            sendXray(['ai-gen', 'gen', 'suc'], { time: seconds });
        }
    } catch (error: any) {
        if (error?.className === 'CancelError') {
            yield* put(addGenerationRequest());
            yield* put(resetImageGeneration());
            sendXray(['ai-gen', 'gen', 'cancel']);
        } else {
            logger.error(error);
            sendXray(['ai-gen', 'gen', 'err']);
            yield* put(
                showSnackbarAction({
                    id: 'ai-photo-generation-failure',
                    text: 'Не удалось сгенерировать — попробуйте написать другой запрос',
                    type: SnackbarTypes.failure,
                    closable: true,
                    hideCloseIcon: true,
                })
            );
            yield* put(resetImageGeneration());
        }
    } finally {
        yield* cancel(intervalTask);
        yield* cancel(cancelWatcher);
    }
}

function* handleSaveDocument(data: Uint8Array, home: string, resultChannel: Channel<any>) {
    try {
        yield* put(
            saveDocument({
                home,
                fileContent: data,
                onResult: (res) => {
                    resultChannel.put(res);
                },
            })
        );

        yield* take(resultChannel);
    } catch (error) {
        resultChannel.put({ error });
    } finally {
        resultChannel.close();
    }
}

function* handleSaveGeneratedImage() {
    const data = aiPhotoGenerationHelper.getImageData();

    if (!data) {
        return;
    }

    const folders = yield* select(getHomeFoldersTree);
    if (!checkAiPhotoGenerationExists(folders)) {
        try {
            yield* call(createFolderRequest, {
                folderName: aiPhotoGenerationFolderName,
                share: false,
                parent: ROOT_FOLDER_ID,
                storage: EStorageType.home,
                gaPrefix: 'ai-photo-generation',
            });
            sendXray(['ai-gen', 'create-folder', 'suc']);
        } catch (error) {
            sendXray(['ai-gen', 'create-folder', 'err']);
        }
    }

    const home = `${aiPhotoGenerationFolderId}/${getAiPhotoGenerationImageFileName()}.jpg`;

    const resultChannel = yield* call(channel);

    yield* fork(handleSaveDocument, data, home, resultChannel);

    const { result, error } = yield take(resultChannel);

    if (error) {
        sendXray(['ai-gen', 'image-save', 'err']);
        logger.error(error);
        yield* put(
            showSnackbarAction({
                id: 'ai-photo-generation-save-failure',
                text: 'Не удалось сохранить изображение. Проверьте подключение к интернету',
                type: SnackbarTypes.failure,
                closable: true,
                hideCloseIcon: true,
            })
        );
        return;
    }

    if (result) {
        closeFullScreenAiPhotoGeneration();

        yield* put(resetImageGeneration());

        yield* put(
            showSnackbarAction({
                id: 'ai-photo-generation-save-success',
                text: 'Сохранено в Библиотеку генераций',
                type: SnackbarTypes.success,
                closable: true,
                hideCloseIcon: true,
                buttonText: 'Перейти',
                onButtonClick: () => opener(aiPhotoGenerationFolderPathFromRoot, true),
            })
        );

        aiPhotoGenerationHelper.resetImageData();

        sendXray(['ai-gen', 'image-save', 'suc']);
    }
}

function* handleAddGenerationRequest() {
    const isPaid = yield select(UserSelectors.isPaidUser);
    const isCorp = yield select(UserSelectors.isCorpUser);
    const limit = isPaid || isCorp ? aiImageGenerationPaidLimit : aiImageGenerationFreeLimit;

    addAiGenerationRequest(limit);
}

export function* watchAiGeneration() {
    yield takeLatest(generateImageStart.toString(), handleGenerateImageStart);
    yield takeLatest(saveGeneratedImage.toString(), handleSaveGeneratedImage);
    yield takeLatest(addGenerationRequest.toString(), handleAddGenerationRequest);
}
